/**
 * Phase 2 · Inventory: live stock with value & low-stock flags, alerts
 * (reorder + expiry), stock movements with manual adjustments, stocktakes
 * with variance posting, and the supplier directory.
 */
import { useCallback, useEffect, useState } from 'react';
import { ClipboardCheck, Plus } from 'lucide-react';
import { http, ApiError } from '../api/client';
import { useAuth } from '../store/auth';
import { useUi } from '../store/ui';
import { money, num, dateTime } from '../lib/format';
import { DataTable, type Column } from '../components/ui/DataTable';
import { Button } from '../components/ui/Button';
import { Badge, StatusBadge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { Input, Select } from '../components/ui/Input';

interface StockRow {
  id: string; name: string; unit_symbol: string; quantity: number;
  reorder_level: number; stock_value: number; is_low: boolean;
  supplier_name: string | null; storage: string | null;
}
interface Movement {
  id: string; ingredient_name: string; type: string; quantity: number;
  unit_symbol: string; reason: string | null; created_at: string; created_by_name: string | null;
}
interface Stocktake {
  id: string; reference: string; status: string; item_count: number;
  variance_value: number; created_at: string; counted_by_name: string | null;
}
interface Supplier { id: string; name: string; contact_person: string | null; phone: string | null; email: string | null }
interface Ingredient { id: string; name: string; unit_symbol: string }

const TABS = ['Stock', 'Alerts', 'Movements', 'Stocktakes', 'Suppliers'] as const;
type Tab = (typeof TABS)[number];

export function InventoryPage() {
  const canAdjust = useAuth((s) => s.user?.permissions.includes('inventory:adjust') ?? false);
  const canCount = useAuth((s) => s.user?.permissions.includes('inventory:stocktake') ?? false);
  const toast = useUi((s) => s.toast);
  const [tab, setTab] = useState<Tab>('Stock');
  const [stock, setStock] = useState<StockRow[]>([]);
  const [totals, setTotals] = useState<{ stockValue: number; lowCount: number }>({ stockValue: 0, lowCount: 0 });
  const [alerts, setAlerts] = useState<{ lowStock: StockRow[]; expiring: { id: string; name: string; batch_no: string; expiry_date: string; remaining: number }[] }>({ lowStock: [], expiring: [] });
  const [movements, setMovements] = useState<Movement[]>([]);
  const [stocktakes, setStocktakes] = useState<Stocktake[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [countFor, setCountFor] = useState<Stocktake | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a, m, t, sup] = await Promise.all([
        http.get<{ stock: StockRow[]; totals: { stockValue: number; lowCount: number } }>('/inventory/stock'),
        http.get<{ lowStock: StockRow[]; expiring: never[] }>('/inventory/alerts'),
        http.get<{ movements: Movement[] }>('/inventory/movements'),
        http.get<{ stocktakes: Stocktake[] }>('/inventory/stocktakes'),
        http.get<{ suppliers: Supplier[] }>('/inventory/suppliers'),
      ]);
      setStock(s.stock); setTotals(s.totals);
      setAlerts(a as never); setMovements(m.movements); setStocktakes(t.stocktakes); setSuppliers(sup.suppliers);
    } catch (e) {
      toast({ kind: 'error', title: 'Load failed', message: e instanceof ApiError ? e.message : String(e) });
    } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { void load(); }, [load]);

  const startStocktake = async () => {
    try {
      const r = await http.post<{ stocktake: Stocktake }>('/inventory/stocktakes');
      toast({ kind: 'success', title: 'Stocktake started', message: r.stocktake.reference });
      setCountFor(r.stocktake);
      await load();
    } catch (e) {
      toast({ kind: 'error', title: 'Failed', message: e instanceof ApiError ? e.message : String(e) });
    }
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Inventory</h1>
          <p className="text-sm text-ink-muted">
            Stock value {money(totals.stockValue)} · {totals.lowCount} low · {alerts.expiring.length} expiring soon
          </p>
        </div>
        <div className="flex gap-2">
          {canAdjust && <Button variant="secondary" onClick={() => setAdjustOpen(true)}><Plus size={16} /> Adjustment</Button>}
          {canCount && <Button onClick={() => void startStocktake()}><ClipboardCheck size={16} /> Start stocktake</Button>}
        </div>
      </header>

      <div className="flex flex-wrap gap-1 rounded-lg bg-surface-1 p-1" role="tablist">
        {TABS.map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${tab === t ? 'bg-surface-0 text-ink shadow-sm' : 'text-ink-muted hover:text-ink'}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'Stock' && (
        <DataTable columns={[
          { key: 'name', header: 'Ingredient', sortable: true },
          { key: 'quantity', header: 'On hand', render: (r: StockRow) => `${num(r.quantity, 2)} ${r.unit_symbol}` },
          { key: 'reorder_level', header: 'Reorder at', render: (r: StockRow) => num(r.reorder_level, 2) },
          { key: 'stock_value', header: 'Value', render: (r: StockRow) => money(r.stock_value) },
          { key: 'supplier_name', header: 'Supplier', render: (r: StockRow) => r.supplier_name ?? '—' },
          { key: 'is_low', header: 'Status', render: (r: StockRow) => r.is_low ? <Badge tone="warning">Low</Badge> : <Badge tone="success">OK</Badge> },
        ]} rows={stock} loading={loading} />
      )}

      {tab === 'Alerts' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <section className="rounded-xl border border-line bg-surface-0 p-4">
            <h2 className="mb-3 text-sm font-semibold text-ink">Below reorder level</h2>
            <DataTable columns={[
              { key: 'name', header: 'Ingredient' },
              { key: 'quantity', header: 'On hand', render: (r: StockRow) => `${num(r.quantity, 2)} ${r.unit_symbol}` },
              { key: 'reorder_level', header: 'Reorder at', render: (r: StockRow) => num(r.reorder_level, 2) },
            ]} rows={alerts.lowStock} loading={loading} />
          </section>
          <section className="rounded-xl border border-line bg-surface-0 p-4">
            <h2 className="mb-3 text-sm font-semibold text-ink">Expiring within 7 days</h2>
            <DataTable columns={[
              { key: 'name', header: 'Ingredient' },
              { key: 'batch_no', header: 'Batch' },
              { key: 'expiry_date', header: 'Expires', render: (r: { expiry_date: string }) => r.expiry_date },
              { key: 'remaining', header: 'Remaining', render: (r: { remaining: number }) => num(r.remaining, 2) },
            ]} rows={alerts.expiring} loading={loading} />
          </section>
        </div>
      )}

      {tab === 'Movements' && (
        <DataTable columns={[
          { key: 'created_at', header: 'When', render: (r: Movement) => dateTime(r.created_at) },
          { key: 'ingredient_name', header: 'Ingredient' },
          { key: 'type', header: 'Type', render: (r: Movement) => <StatusBadge status={r.type} /> },
          { key: 'quantity', header: 'Qty', render: (r: Movement) => `${num(r.quantity, 2)} ${r.unit_symbol}` },
          { key: 'reason', header: 'Reason', render: (r: Movement) => r.reason ?? '—' },
          { key: 'created_by_name', header: 'By', render: (r: Movement) => r.created_by_name ?? '—' },
        ]} rows={movements} loading={loading} />
      )}

      {tab === 'Stocktakes' && (
        <DataTable columns={[
          { key: 'reference', header: 'Reference', sortable: true },
          { key: 'status', header: 'Status', render: (r: Stocktake) => <StatusBadge status={r.status} /> },
          { key: 'item_count', header: 'Lines' },
          { key: 'variance_value', header: 'Variance value', render: (r: Stocktake) => money(r.variance_value) },
          { key: 'created_at', header: 'Started', render: (r: Stocktake) => dateTime(r.created_at) },
          ...(canCount ? [{
            key: 'act', header: '', render: (r: Stocktake) => r.status === 'counting'
              ? <div className="flex justify-end"><Button size="sm" variant="secondary" onClick={() => setCountFor(r)}>Count & post</Button></div>
              : null,
          }] as Column<Stocktake>[] : []),
        ]} rows={stocktakes} loading={loading} />
      )}

      {tab === 'Suppliers' && (
        <DataTable columns={[
          { key: 'name', header: 'Supplier', sortable: true },
          { key: 'contact_person', header: 'Contact', render: (r: Supplier) => r.contact_person ?? '—' },
          { key: 'phone', header: 'Phone', render: (r: Supplier) => r.phone ?? '—' },
          { key: 'email', header: 'Email', render: (r: Supplier) => r.email ?? '—' },
        ]} rows={suppliers} loading={loading} />
      )}

      {adjustOpen && <AdjustModal onClose={() => setAdjustOpen(false)} onSaved={async () => { setAdjustOpen(false); await load(); }} />}
      {countFor && <CountModal stocktake={countFor} onClose={() => setCountFor(null)} onSaved={async () => { setCountFor(null); await load(); }} />}
    </div>
  );
}

function AdjustModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => Promise<void> }) {
  const toast = useUi((s) => s.toast);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [ingredientId, setIngredientId] = useState('');
  const [type, setType] = useState<'adjustment' | 'wastage' | 'spoilage' | 'return'>('adjustment');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void http.get<{ ingredients: Ingredient[] }>('/inventory/ingredients').then((r) => {
      setIngredients(r.ingredients);
      setIngredientId(r.ingredients[0]?.id ?? '');
    });
  }, []);
  const unit = ingredients.find((i) => i.id === ingredientId)?.unit_symbol ?? 'pcs';

  const save = async () => {
    setBusy(true);
    try {
      await http.post('/inventory/movements', {
        ingredientId, type, quantity: parseFloat(quantity || '0'), unitSymbol: unit,
        reason: reason || (type === 'adjustment' ? 'Manual adjustment' : type),
      });
      toast({ kind: 'success', title: 'Movement recorded' });
      await onSaved();
    } catch (e) {
      toast({ kind: 'error', title: 'Failed', message: e instanceof ApiError ? e.message : String(e) });
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title="Record stock movement"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={busy} onClick={() => void save()}>Record</Button></>}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Select label="Ingredient" value={ingredientId} onChange={(e) => setIngredientId(e.target.value)}>
          {ingredients.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
        </Select>
        <Select label="Type" value={type} onChange={(e) => setType(e.target.value as never)}>
          <option value="adjustment">Adjustment (+/−)</option>
          <option value="wastage">Wastage</option>
          <option value="spoilage">Spoilage</option>
          <option value="return">Return</option>
        </Select>
        <Input label={`Quantity (${unit})`} type="number" step="any" min="0" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
        <Input label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
      </div>
    </Modal>
  );
}

function CountModal({ stocktake, onClose, onSaved }: { stocktake: Stocktake; onClose: () => void; onSaved: () => Promise<void> }) {
  const toast = useUi((s) => s.toast);
  const [items, setItems] = useState<{ ingredient_id: string; name: string; unit_symbol: string; system_qty: number }[]>([]);
  const [counts, setCounts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void http.get<{ items: typeof items }>(`/inventory/stocktakes/${stocktake.id}`).then((r) => {
      setItems(r.items as never);
      setCounts(Object.fromEntries((r.items as never as { ingredient_id: string; system_qty: number }[]).map((i) => [i.ingredient_id, String(i.system_qty)])));
    });
  }, [stocktake.id]);

  const post = async () => {
    setBusy(true);
    try {
      await http.post(`/inventory/stocktakes/${stocktake.id}/post`, {
        counts: items.map((i) => ({ ingredientId: i.ingredient_id, countedQty: parseFloat(counts[i.ingredient_id] ?? '0') })),
      });
      toast({ kind: 'success', title: 'Stocktake posted', message: stocktake.reference });
      await onSaved();
    } catch (e) {
      toast({ kind: 'error', title: 'Post failed', message: e instanceof ApiError ? e.message : String(e) });
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Count ${stocktake.reference}`} size="lg"
      footer={<><Button variant="secondary" onClick={onClose}>Later</Button><Button loading={busy} onClick={() => void post()}>Post counts</Button></>}>
      <div className="max-h-96 space-y-2 overflow-y-auto pr-1">
        {items.map((i) => (
          <div key={i.ingredient_id} className="grid grid-cols-[1fr_7rem_7rem] items-center gap-2 text-sm">
            <span className="truncate text-ink">{i.name}</span>
            <span className="text-xs text-ink-muted">sys {num(i.system_qty, 2)} {i.unit_symbol}</span>
            <Input aria-label={`Counted ${i.name}`} type="number" step="any" min="0" value={counts[i.ingredient_id] ?? ''}
              onChange={(e) => setCounts((c) => ({ ...c, [i.ingredient_id]: e.target.value }))} />
          </div>
        ))}
      </div>
    </Modal>
  );
}
